import "./styles.scss";

import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ImageLightbox from "components/ImageLightbox";
import classnames from "classnames";

import TextEditor, {
  htmlToDraftBlocks,
  convertToHTML,
  getPlainText,
} from "components/TextEditor";

import useResponsive from "libs/helpers/useResponsive";

const mainClass = "message-submitter";

const MessageSubmitter = ({
  file_progress,
  sendMessage,
  isMessageSending,
  droppedFiles,
  onFilesDropHandled,
}) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const ref = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const isUploading = file_progress !== undefined && file_progress !== null;

  const [content, setContent] = useState(htmlToDraftBlocks());
  const [files, setFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (droppedFiles) setIsOpen(true);
  }, [droppedFiles]);

  useEffect(() => {
    const handleClose = (e) => {
      const submitter = ref.current;
      const isModal = e.target.closest(".ReactModalPortal");

      if (submitter && !submitter.contains(e.target) && !isModal)
        setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClose);
    }

    return () => {
      document.removeEventListener("mousedown", handleClose);
    };
  }, [isOpen]);

  const placeholder = `${t("Write a comment")}...`;

  const html = convertToHTML(content);
  const plainText = getPlainText(html);
  const isEmpty = plainText === "";

  const handleSubmit = async () => {
    if (typeof sendMessage === "function") await sendMessage(html, files);

    setContent(htmlToDraftBlocks());
    setIsOpen(false);
    setFiles([]);
  };

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--disabled`]: isUploading || isMessageSending,
        })}
        ref={ref}
      >
        {!max.sm && (
          <button
            className={classnames(`${mainClass}__button`, {
              [`${mainClass}__button--filled`]: !isEmpty,
            })}
            type="button"
            onClick={() => setIsOpen(true)}
          >
            {!isEmpty ? `${t("Draft")}: ${plainText}` : placeholder}
            {files.length > 0 && (
              <small>
                {t("Attachments")}: {files.length}
              </small>
            )}
            <span>{t("Send")}</span>
          </button>
        )}
        {(isOpen || droppedFiles || max.sm) && (
          <div className={`${mainClass}__editor`}>
            <TextEditor
              id="messages"
              placeholder={placeholder}
              content={content}
              setContent={setContent}
              files={files}
              setFiles={setFiles}
              droppedFiles={droppedFiles}
              onFilesDropHandled={onFilesDropHandled}
              autoFocus
              isDisabled={isUploading}
              isMessageSending={isMessageSending}
              isSubmitDisabled={isEmpty && !(files.length > 0)}
              onPreviewClick={(url) => setLightbox(url)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      {!!lightbox && (
        <ImageLightbox
          src={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default MessageSubmitter;
